import {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
  memo,
  type FormEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react";
import { ArrowDown, Boot, ChatTeardropText, Check, Flag, PaperPlaneTilt, Prohibit, SmileySticker, Warning, X } from "@phosphor-icons/react";
import {
  REACTION_EMOJIS,
  REPORT_REASONS,
  type ChatBroadcastMessage,
  type ChatReplyRef,
  type ModerationAction,
  type PollState,
  type RoomUser,
  type VoteState,
  LIMITS,
} from "@yay/shared";
import { ChatText } from "./ChatText";
import { Censored } from "./Censored";
import { useBlocked } from "../lib/blockPref";

const MOD_VERBS: Record<string, string> = {
  kick: "kicked",
  kicked: "kicked",
  mute: "muted",
  muted: "muted",
  unmute: "unmuted",
  unmuted: "unmuted",
  ban: "banned",
  banned: "banned",
  unban: "unbanned",
  unbanned: "unbanned",
  transfer: "transferred the room to",
};

function normalizeModAction(action: string): string {
  if (action === "kicked") return "kick";
  if (action === "banned") return "ban";
  if (action === "muted") return "mute";
  if (action === "unmuted") return "unmute";
  if (action === "unbanned") return "unban";
  return action;
}

function dylanSrc(seed: string): string {
  return `https://api.dicebear.com/10.x/dylan/svg?seed=${encodeURIComponent(seed)}`;
}

function userByName(
  users: RoomUser[],
  me: RoomUser | null,
  name: string,
): RoomUser | null {
  const n = name.toLowerCase();
  if (me && me.username.toLowerCase() === n) return me;
  return users.find((u) => u.username.toLowerCase() === n) ?? null;
}

function parsePollClosed(text: string): {
  title: string;
  rows: { label: string; n: number }[];
  mode: "won" | "tie" | "empty";
  winner?: string;
} | null {
  const m = text.match(/^poll closed:\s*(.*?)\s+—\s*(.*)$/u);
  if (!m) return null;
  const title = m[1]!.trim();
  const rest = m[2]!.trim();
  if (rest === "no votes") {
    return { title, rows: [], mode: "empty" };
  }
  const paren = rest.match(/^(.*?)\s+\((.*)\)\s*$/u);
  const breakdownSrc = paren?.[2] ?? rest;
  const rows = breakdownSrc.split(" · ").map((part) => {
    const sm = part.trim().match(/^(.*)\s+(\d+)$/u);
    return sm
      ? { label: sm[1]!.trim(), n: Number(sm[2]) }
      : { label: part.trim(), n: 0 };
  });
  if (rest.startsWith("tie")) {
    return { title, rows, mode: "tie" };
  }
  const winner = paren?.[1]?.replace(/\s+won$/u, "").trim();
  return { title, rows, mode: "won", winner };
}

function PollClosedCard({ text }: { text: string }) {
  const parsed = parsePollClosed(text);
  if (!parsed) return <>{text}</>;
  const total = parsed.rows.reduce((n, r) => n + r.n, 0);
  return (
    <div className="chat-poll-result">
      <div className="chat-poll-result-kicker">Poll closed</div>
      <div className="chat-poll-result-title">{parsed.title}</div>
      {parsed.mode === "empty" ? (
        <div className="chat-poll-result-empty">no votes</div>
      ) : (
        <div className="chat-poll-result-rows">
          {parsed.rows.map((row) => {
            const pct = total === 0 ? 0 : Math.round((row.n / total) * 100);
            const win =
              parsed.mode === "won"
                ? row.label === parsed.winner
                : parsed.mode === "tie" &&
                  row.n === Math.max(...parsed.rows.map((r) => r.n));
            return (
              <button
                key={row.label}
                type="button"
                className={`chat-poll-result-row${win ? " win" : ""}`}
              >
                <span
                  className="chat-poll-result-fill"
                  style={{ width: `${pct}%` }}
                />
                <span className="chat-poll-result-label">{row.label}</span>
                <span className="chat-poll-result-n">
                  {row.n} · {pct}%
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NamedUser({
  name,
  users,
  me,
}: {
  name: string;
  users: RoomUser[];
  me: RoomUser | null;
}) {
  const u = userByName(users, me, name);
  return (
    <span
      className="chat-named"
      style={u?.color ? { color: u.color } : undefined}
    >
      <Censored text={name} />
    </span>
  );
}

function SystemLine({
  text,
  users,
  me,
}: {
  text: string;
  users: RoomUser[];
  me: RoomUser | null;
}) {
  const names = [...(me ? [me] : []), ...users]
    .map((u) => u.username)
    .filter(
      (n, i, a) =>
        a.findIndex((x) => x.toLowerCase() === n.toLowerCase()) === i,
    )
    .sort((a, b) => b.length - a.length);
  if (names.length === 0) return <>{text}</>;
  const re = new RegExp(
    `(?<![A-Za-z0-9_])(${names.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|")})(?![A-Za-z0-9_])`,
    "gi",
  );
  const parts = text.split(re);
  return (
    <>
      {parts.map((p, i) => {
        if (!p) return null;
        const hit = names.find((n) => n.toLowerCase() === p.toLowerCase());
        if (hit) {
          return <NamedUser key={i} name={p} users={users} me={me} />;
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}

/** Slash palette — the discoverable command list (typed "/" in the composer). */
const SLASH_COMMANDS = [
  { cmd: "kick", desc: "kick a user — owners act, guests start a room vote" },
  { cmd: "ban", desc: "ban a user — owners act, guests start a room vote" },
  { cmd: "mute", desc: "mute or unmute a user (room owner)" },
  { cmd: "unban", desc: "lift a room ban (room owner)" },
  { cmd: "whisper", desc: "send a whisper to a user" },
  { cmd: "poll", desc: "create a poll" },
  { cmd: "lock", desc: "set a room password (owner) — /lock secret" },
  { cmd: "unlock", desc: "remove the room password (owner)" },
  { cmd: "help", desc: "show available commands" },
] as const;
import { StickerPicker, getStickerSrc } from "./StickerPicker";
import type { DisplayChatItem, PendingChatMessage } from "../hooks/useRoom";

const NEAR_BOTTOM_PX = 80;

function distanceFromBottom(el: HTMLElement) {
  return el.scrollHeight - el.scrollTop - el.clientHeight;
}

interface ChatPanelProps {
  messages: DisplayChatItem[];
  users: RoomUser[];
  me: RoomUser | null;
  onSend: (text: string, replyToId?: string) => boolean;
  onRetry?: (clientId: string) => boolean;
  onReact?: (messageId: string, emoji: string) => boolean;
  /** Report a chat author (moderation). Target resolved from `users` by username. */
  onReport?: (targetUserId: string, reason: string) => boolean;
  /** Server-driven report delivery outcome (pending/sent/failed). */
  reportOutcome?:
    | { status: "pending" }
    | { status: "sent" }
    | { status: "failed" }
    | null;
  disabled?: boolean;
  sessionReady?: boolean;
  connectionStatus?: string;
  /** Server rate-limit unlock time (ms). Locks composer until elapsed. */
  rateLimitUntil?: number;
  /** True while rate-limit cooldown is active. */
  chatLocked?: boolean;
  /** Sidebar open — panel stays mounted while collapsed, so pause must ignore that. */
  chatOpen?: boolean;
  /** Slash system (Phase 1.3). */
  isOwner?: boolean;
  /** Private room — moderation commands don't exist in the global lobby
   * (no owner there); gate the picker instead of running it into the
   * server's refusal. */
  inPrivateRoom?: boolean;
  /** Current user is muted by the owner — composer replaced by a notice. */
  muted?: boolean;
  /** Active poll to float above the composer, or null. */
  poll?: PollState | null;
  /** Banned members (id + username) — the /unban picker's target list.
   * Banned users are never in `users` (they can't be in the room), so the
   * owner needs this separate list to lift a ban. */
  bannedUsers?: Array<{
    id: string;
    username: string;
    avatarSeed?: string;
  }>;
  onCommand?: (action: ModerationAction, targetIds: string[]) => boolean;
  onWhisper?: (targetUserId: string, text: string) => boolean;
  onPollCreate?: (title: string, options: string[]) => boolean;
  onPollVote?: (pollId: string, optionIndex: number) => boolean;
  onPollClose?: (pollId: string) => boolean;
  /** Active guest-moderation vote (kick/ban), or null. */
  vote?: VoteState | null;
  onVoteCast?: (voteId: string, approve: boolean) => boolean;
}

function ReplyQuote({
  reply,
  meUsername,
}: {
  reply: ChatReplyRef;
  meUsername?: string | null;
}) {
  return (
    <div className="chat-reply-quote">
      <span className="chat-reply-user">
        <Censored text={reply.username} />
      </span>
      <span className="chat-reply-text">
        <ChatText text={reply.text} meUsername={meUsername} compact />
      </span>
    </div>
  );
}

function MessageReactions({
  msg,
  me,
  users,
  onReact,
}: {
  msg: ChatBroadcastMessage;
  me: RoomUser | null;
  users: RoomUser[];
  onReact?: (messageId: string, emoji: string) => boolean;
}) {
  const reactions = msg.reactions ?? {};
  const entries = Object.entries(reactions).filter(([, ids]) => ids.length > 0);
  if (entries.length === 0) return null;

  return (
    <div className="chat-reactions">
      {entries.map(([emoji, ids]) => {
        const mine = me ? ids.includes(me.id) : false;
        // Resolve names from presence; ids of users who left show as count only.
        const names = users
          .filter((u) => ids.includes(u.id))
          .map((u) => u.username);
        const title =
          names.length >= ids.length
            ? names.join(", ")
            : `${names.join(", ")}${names.length > 0 ? " +" : ""} ${ids.length - names.length} more`;
        return (
          <button
            key={emoji}
            type="button"
            className={`chat-reaction-pill${mine ? " mine" : ""}`}
            title={title}
            onClick={() => onReact?.(msg.id, emoji)}
          >
            <span>{emoji}</span>
            <span className="chat-reaction-count">{ids.length}</span>
          </button>
        );
      })}
    </div>
  );
}

function extractContent(el: HTMLDivElement): string {
  const parts: string[] = [];
  for (const node of el.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push((node.textContent ?? "").replace(/\u00A0/g, " "));
    } else if (node instanceof HTMLImageElement) {
      parts.push(node.getAttribute("data-sticker") ?? "");
    } else if (node instanceof HTMLBRElement) {
      parts.push("");
    } else {
      parts.push(node.textContent ?? "");
    }
  }
  return parts.join("");
}

/** Text before the caret inside a contentEditable. */
function textBeforeCaret(el: HTMLDivElement): string | null {
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !el.contains(sel.anchorNode)) return null;
  const caret = sel.getRangeAt(0);
  const pre = caret.cloneRange();
  pre.selectNodeContents(el);
  pre.setEnd(caret.endContainer, caret.endOffset);
  return pre.toString().replace(/\u00A0/g, " ");
}

/** Active @query at the caret, if any (`@` + partial name). */
function getActiveMention(el: HTMLDivElement): { query: string; matched: string } | null {
  const before = textBeforeCaret(el);
  if (before === null) return null;
  const match = before.match(/(^|[\s\u00A0])@([A-Za-z0-9_.-]*)$/);
  if (!match) return null;
  return { query: match[2] ?? "", matched: `@${match[2] ?? ""}` };
}

/** Replace the active @query with `@username `. */
function replaceActiveMention(el: HTMLDivElement, username: string): boolean {
  const active = getActiveMention(el);
  if (!active) return false;
  const sel = window.getSelection();
  if (!sel || !sel.rangeCount || !el.contains(sel.anchorNode)) return false;

  // Extend selection backward over `@query`, then overwrite.
  sel.collapse(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
  for (let i = 0; i < active.matched.length; i++) {
    sel.modify("extend", "backward", "character");
  }
  document.execCommand("insertText", false, `@${username} `);
  return true;
}

export const ChatPanel = memo(function ChatPanel({
  messages,
  users,
  me,
  onSend,
  onRetry,
  onReact,
  onReport,
  reportOutcome,
  disabled,
  sessionReady = true,
  connectionStatus = "connected",
  rateLimitUntil = 0,
  chatLocked = false,
  chatOpen = true,
  isOwner = false,
  inPrivateRoom = false,
  muted = false,
  poll = null,
  bannedUsers = [],
  onCommand,
  onWhisper,
  onPollCreate,
  onPollVote,
  onPollClose,
  vote = null,
  onVoteCast,
}: ChatPanelProps) {
  const [stickerOpen, setStickerOpen] = useState(false);
  const [leavingPoll, setLeavingPoll] = useState<PollState | null>(null);
  const pollRef = useRef(poll);
  useEffect(() => {
    const prev = pollRef.current;
    pollRef.current = poll;
    if (poll && !poll.closed) {
      setLeavingPoll(null);
      return;
    }
    const snapshot =
      prev && !prev.closed ? prev : poll && poll.closed ? poll : null;
    if (!snapshot) return;
    setLeavingPoll(snapshot);
    const t = window.setTimeout(() => setLeavingPoll(null), 320);
    return () => window.clearTimeout(t);
  }, [poll]);
  const shownPoll = poll && !poll.closed ? poll : leavingPoll;
  const pollLeaving = Boolean(shownPoll && !(poll && !poll.closed));
  const [isEmpty, setIsEmpty] = useState(true);
  const [paused, setPaused] = useState(false);
  const [unseenCount, setUnseenCount] = useState(0);
  const [replyTo, setReplyTo] = useState<ChatReplyRef | null>(null);
  const [reactMenuId, setReactMenuId] = useState<string | null>(null);
  const [reportingId, setReportingId] = useState<string | null>(null);
  /** Message the current report outcome applies to (one pending at a time). */
  const [reportedId, setReportedId] = useState<string | null>(null);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [toast, setToast] = useState<{ text: string; visible: boolean }>({
    text: "",
    visible: false,
  });
  /** 1s heartbeat while a poll/vote is open — keeps the countdown labels
   * ("2m left", "37s left") honest between broadcasts. Without it the label
   * froze at the last render and could sit stale past the server-side TTL. */
  const [, setNowTick] = useState(0);
  useEffect(() => {
    if (!poll && !vote) return;
    const id = window.setInterval(() => setNowTick((t) => t + 1), 1_000);
    return () => window.clearInterval(id);
  }, [poll, vote]);

  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const whisperTextRef = useRef<HTMLInputElement>(null);
  const toastTimerRef = useRef<number | null>(null);
  const pausedRef = useRef(false);
  /** Last message identity — length alone fails once the ring buffer is full. */
  const prevTailKeyRef = useRef<string | null>(null);
  /** Ignore scroll events caused by our own stick-to-bottom writes. */
  const stickingRef = useRef(false);
  const stickGenRef = useRef(0);
  const chatOpenRef = useRef(chatOpen);

  pausedRef.current = paused;
  chatOpenRef.current = chatOpen;

  const mentionSuggestions =
    mentionQuery === null
      ? []
      : users
          .filter((u) =>
            u.username.toLowerCase().startsWith(mentionQuery.toLowerCase()),
          )
          .slice(0, 8);

  const hasSending = messages.some(
    (m) => m.type === "pending" && m.status === "sending",
  );
  // Muted is a hard lock like disabled: the owner turned off chat, so the
  // composer must not keep accepting input (the server rejects muted sends —
  // an active-looking composer whose messages all die as "Not delivered" is
  // a dead-end UX).
  const inputLocked = disabled || chatLocked || muted;
  const showSyncBanner =
    connectionStatus === "connecting" ||
    connectionStatus === "disconnected" ||
    (connectionStatus === "connected" && !sessionReady) ||
    hasSending;

  const syncLabel =
    connectionStatus === "disconnected" || connectionStatus === "connecting"
      ? "Reconnecting…"
      : !sessionReady
        ? "Restoring session…"
        : hasSending
          ? "Sending…"
          : "";

  /** Collapsed sidebar still mounts us at width 0 — metrics lie and trip pause. */
  function listIsUsable(el: HTMLElement | null): el is HTMLElement {
    return !!el && el.clientWidth >= 32 && el.clientHeight >= 32;
  }

  const beginStick = useCallback(() => {
    stickingRef.current = true;
    const gen = ++stickGenRef.current;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (stickGenRef.current === gen) stickingRef.current = false;
      });
    });
  }, []);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (!el) return;
    beginStick();
    el.scrollTop = el.scrollHeight;
    setPaused(false);
    setUnseenCount(0);
  }, [beginStick]);

  const isNearBottom = useCallback(() => {
    const el = listRef.current;
    if (!listIsUsable(el)) return true;
    return distanceFromBottom(el) <= NEAR_BOTTOM_PX;
  }, []);

  // Stay pinned while following — sticker loads, sync banner, reactions, etc.
  useEffect(() => {
    const el = listRef.current;
    const content = contentRef.current;
    if (!el || !content) return;

    const stickIfFollowing = () => {
      if (pausedRef.current) return;
      if (!chatOpenRef.current) return;
      if (!listIsUsable(el)) return;
      beginStick();
      el.scrollTop = el.scrollHeight;
    };

    const ro = new ResizeObserver(stickIfFollowing);
    ro.observe(content);
    ro.observe(el);
    return () => ro.disconnect();
  }, [beginStick]);

  // Sidebar reopen after collapse/mode flips — resume live follow.
  useEffect(() => {
    if (!chatOpen) return;
    let cancelled = false;
    const tryStick = () => {
      if (cancelled) return;
      if (!listIsUsable(listRef.current)) {
        requestAnimationFrame(tryStick);
        return;
      }
      scrollToBottom();
    };
    const raf = requestAnimationFrame(tryStick);
    // Width transition is 350ms — pin again once the column has settled.
    const timer = window.setTimeout(tryStick, 400);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      clearTimeout(timer);
    };
  }, [chatOpen, scrollToBottom]);

  useEffect(() => {
    const tail = messages[messages.length - 1];
    const tailKey = !tail
      ? null
      : tail.type === "pending"
        ? `pending:${tail.clientId}`
        : tail.type === "chat"
          ? `chat:${tail.id}`
          : `sys:${tail.timestamp}:${"text" in tail ? tail.text : tail.type}`;

    if (tailKey === prevTailKeyRef.current) return;
    const hadTail = prevTailKeyRef.current !== null;
    prevTailKeyRef.current = tailKey;
    if (!tailKey) return;

    // First paint — pin once content exists.
    if (!hadTail) {
      scrollToBottom();
      return;
    }

    if (pausedRef.current) {
      setUnseenCount((c) => c + 1);
      return;
    }

    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!reactMenuId && !reportingId) return;
    const close = () => {
      setReactMenuId(null);
      setReportingId(null);
    };
    window.addEventListener("click", close);
    return () => window.removeEventListener("click", close);
  }, [reactMenuId, reportingId]);

  const checkEmpty = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    setIsEmpty(extractContent(el).trim() === "");
  }, []);

  // ── Slash commands (Phase 1.3) ─────────────────────────────────────
  const [cmd, setCmd] = useState<{
    action: ModerationAction | "whisper";
    targets: string[];
  } | null>(null);
  const [cmdQuery, setCmdQuery] = useState("");
  const [cmdIndex, setCmdIndex] = useState(0);
  /** Whisper message draft (whisper command mode is two-step: pick → type). */
  const [cmdText, setCmdText] = useState("");
  /** Open slash palette: the partial word after "/" ("" = show all). */
  const [slashMenu, setSlashMenu] = useState<string | null>(null);
  const [slashIndex, setSlashIndex] = useState(0);
  const resolveChatUser = useCallback(
    (username: string) => userByName(users, me, username),
    [users, me],
  );
  /** Personal mute: blocked usernames (Discord-style collapse). */
  const blocked = useBlocked();
  /** Blocked messages the user chose to reveal (per message id). */
  const [revealedBlocked, setRevealedBlocked] = useState<Set<string>>(
    () => new Set(),
  );
  /** Discord-style: reveal ONE blocked message the user clicked on. */
  function revealBlocked(id: string) {
    setRevealedBlocked((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }
  const [pollDraft, setPollDraft] = useState<{
    title: string;
    options: string[];
  } | null>(null);

  /** Single toast timer — a new toast always replaces the old one. */
  function showToast(text: string, ms = 3000) {
    if (toastTimerRef.current !== null) {
      window.clearTimeout(toastTimerRef.current);
    }
    setToast({ text, visible: true });
    toastTimerRef.current = window.setTimeout(() => {
      toastTimerRef.current = null;
      setToast({ text: "", visible: false });
    }, ms);
  }

  /**
   * Target suggestions. Empty query deliberately shows NOTHING: with an
   * empty filter, Enter must RUN the command (or focus the whisper message)
   * — showing every user made Enter re-pick a random target instead.
   */
  const cmdSuggestions = useMemo(() => {
    if (!cmd) return [];
    const q = cmdQuery.trim().toLowerCase();
    if (!q) return [];
    // /unban picks from the BAN LIST (banned users are never in `users`);
    // every other command picks from live presence.
    const pool = cmd.action === "unban" ? bannedUsers : users;
    return pool
      .filter((u) => u.id !== me?.id && !cmd.targets.includes(u.id))
      .filter((u) => u.username.toLowerCase().includes(q))
      .slice(0, 6);
  }, [cmd, cmdQuery, users, me, bannedUsers]);

  const slashSuggestions = useMemo(() => {
    if (slashMenu === null) return [];
    const q = slashMenu.toLowerCase();
    return SLASH_COMMANDS.filter((c) => !q || c.cmd.startsWith(q));
  }, [slashMenu]);

  function exitCommandMode() {
    setCmd(null);
    setCmdQuery("");
    setCmdIndex(0);
    setCmdText("");
    setSlashMenu(null);
  }

  function clearInput() {
    const el = inputRef.current;
    if (el) {
      el.innerHTML = "";
      setIsEmpty(true);
      setStickerOpen(false);
    }
    setSlashMenu(null);
  }

  /**
   * Enter command mode when the input starts with a known slash command.
   * While a mode is active, re-syncs the live filter query on every keystroke
   * (typing "/kick al" must actually filter — the query was previously frozen
   * at the first matching keystroke, so the picker always showed everyone).
   */
  function syncCommandState() {
    const el = inputRef.current;
    if (!el) return;
    const content = extractContent(el);
    const trimmed = content.trim();

    if (cmd) {
      // Whisper text lives in the dedicated input; the composer is cleared
      // once a target is attached, so there's nothing to re-sync.
      if (cmd.action === "whisper" && cmd.targets.length > 0) return;
      const m = content.match(/^\/(kick|mute|ban|unban)\b\s*(.*)$/i);
      if (m && m[1]!.toLowerCase() === cmd.action) {
        setCmdQuery(m[2] ?? "");
        return;
      }
      if (cmd.action === "whisper" && cmd.targets.length === 0) {
        const wm = content.match(/^\/whisper\s*(.*)$/i);
        if (wm) {
          const namePart = (wm[1] ?? "").match(/^@?([\w.-]+)/i)?.[1] ?? "";
          setCmdQuery(namePart);
          return;
        }
      }
      // Chip mode: composer is a name filter, not another slash command.
      if (!trimmed.startsWith("/")) {
        setCmdQuery(content);
        return;
      }
      exitCommandMode();
      return;
    }

    if (pollDraft) return;

    // Moderation commands — /kick and /ban are open to everyone in private
    // rooms (guests start a room VOTE; the owner acts directly — the server
    // decides). /mute and /unban are room-wide owner powers only. The gate
    // just keeps people out of a picker that can't run. The global lobby has
    // no owner and no moderation — gate before the picker opens.
    const m = content.match(/^\/(kick|ban)\b\s*(.*)$/i);
    if (m) {
      if (!inPrivateRoom) {
        showToast("moderation isn't available in the global lobby", 3000);
        clearInput();
        return;
      }
      setSlashMenu(null);
      setCmd({
        action: m[1]!.toLowerCase() as ModerationAction,
        targets: [],
      });
      setCmdQuery(m[2] ?? "");
      setCmdIndex(0);
      clearInput();
      return;
    }
    const mo = content.match(/^\/(mute|unban)\b\s*(.*)$/i);
    if (mo) {
      if (!inPrivateRoom) {
        showToast("moderation isn't available in the global lobby", 3000);
        clearInput();
        return;
      }
      if (!isOwner) {
        showToast("only the room owner can moderate", 3000);
        clearInput();
        return;
      }
      setSlashMenu(null);
      setCmd({
        action: mo[1]!.toLowerCase() as ModerationAction,
        targets: [],
      });
      setCmdQuery(mo[2] ?? "");
      setCmdIndex(0);
      clearInput();
      return;
    }

    // /whisper — two-step: pick a target (chips), then type the message.
    const wm = content.match(/^\/whisper\s*(.*)$/i);
    if (wm) {
      const namePart = (wm[1] ?? "").match(/^@?([\w.-]+)/i)?.[1] ?? "";
      setSlashMenu(null);
      setCmd({ action: "whisper", targets: [] });
      setCmdQuery(namePart);
      setCmdIndex(0);
      setCmdText("");
      clearInput();
      return;
    }

    if (/^\/poll$/i.test(trimmed)) {
      setSlashMenu(null);
      setPollDraft({ title: "", options: ["", ""] });
      clearInput(); // the draft replaces the composer text
      return;
    }

    if (/^\/help$/i.test(trimmed)) {
      setSlashMenu(null);
      showToast("/kick /mute /ban /unban /whisper /poll /lock /unlock /help", 4000);
      return;
    }

    // Unknown bare /word → slash palette (discoverability). Only opens when
    // something matches; otherwise the text is just a normal message.
    if (/^\/[a-z]*$/i.test(trimmed)) {
      const q = trimmed.slice(1).toLowerCase();
      const matches = SLASH_COMMANDS.filter((c) => !q || c.cmd.startsWith(q));
      if (matches.length > 0) {
        setSlashMenu(q);
        setSlashIndex(0);
      } else {
        setSlashMenu(null);
      }
      return;
    }
    setSlashMenu(null);
  }

  function pickSlashCommand(name: (typeof SLASH_COMMANDS)[number]["cmd"]) {
    clearInput();
    setSlashMenu(null);
    setSlashIndex(0);
    if (name === "poll") {
      setPollDraft({ title: "", options: ["", ""] });
      return;
    }
    if (name === "help") {
      showToast("/kick /mute /ban /unban /whisper /poll /lock /unlock /help", 4000);
      return;
    }
    if (name === "lock") {
      if (!inPrivateRoom || !isOwner) {
        showToast(
          inPrivateRoom
            ? "only the room owner can set a password"
            : "the lobby can't have a password",
          3000,
        );
        return;
      }
      showToast("type /lock and the password, then enter", 4000);
      return;
    }
    if (name === "unlock") {
      if (!inPrivateRoom || !isOwner) {
        showToast(
          inPrivateRoom
            ? "only the room owner can remove the password"
            : "the lobby can't have a password",
          3000,
        );
        return;
      }
      onSend?.("/unlock");
      return;
    }
    if (name === "kick" || name === "ban") {
      if (!inPrivateRoom) {
        showToast("moderation isn't available in the global lobby", 3000);
        return;
      }
      setCmd({ action: name, targets: [] });
      setCmdQuery("");
      setCmdIndex(0);
      inputRef.current?.focus();
      return;
    }
    if (name === "mute" || name === "unban") {
      if (!inPrivateRoom) {
        showToast("moderation isn't available in the global lobby", 3000);
        return;
      }
      if (!isOwner) {
        showToast("only the room owner can moderate", 3000);
        return;
      }
      setCmd({ action: name, targets: [] });
      setCmdQuery("");
      setCmdIndex(0);
      inputRef.current?.focus();
      return;
    }
    setCmd({ action: "whisper", targets: [] });
    setCmdQuery("");
    setCmdIndex(0);
    setCmdText("");
    inputRef.current?.focus();
  }

  function addCmdTarget(u: { id: string; username: string }) {
    // Whisper targets one user — picking replaces, not appends.
    if (cmd?.action === "whisper") {
      setCmd({ action: "whisper", targets: [u.id] });
    } else {
      setCmd((prev) =>
        prev ? { ...prev, targets: [...prev.targets, u.id] } : prev,
      );
    }
    setCmdQuery("");
    setCmdIndex(0);
    clearInput();
    if (cmd?.action === "whisper") {
      requestAnimationFrame(() => whisperTextRef.current?.focus());
    } else {
      inputRef.current?.focus();
    }
  }

  function sendWhisper() {
    if (!cmd || cmd.action !== "whisper" || cmd.targets.length === 0) return;
    const text = cmdText.trim();
    if (!text) return;
    onWhisper?.(cmd.targets[0]!, text);
    exitCommandMode();
    clearInput();
  }

  function handleScroll() {
    if (stickingRef.current) return;
    if (!chatOpenRef.current) return;
    if (!listIsUsable(listRef.current)) return;
    if (isNearBottom()) {
      if (paused) {
        setPaused(false);
        setUnseenCount(0);
      }
    } else if (!paused) {
      setPaused(true);
    }
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (inputLocked) return;

    const el = inputRef.current;
    if (!el) return;

    const content = extractContent(el);
    const trimmed = content.trim();
    if (!trimmed) return;
    if (pollDraft) return; // draft mode owns the composer
    if (cmd) {
      // Command mode owns the composer — the Send button just exits it
      // (raw "/kick al" must never go out as chat text).
      exitCommandMode();
      clearInput();
      return;
    }

    // Owner-only moderation commands never go out as plain chat. The lobby
    // has no owner — say so instead of the misleading owner toast.
    if (/^\/(kick|mute|ban|unban)\b/i.test(trimmed)) {
      if (!inPrivateRoom) {
        showToast("moderation isn't available in the global lobby", 3000);
        return;
      }
      if (!isOwner) {
        showToast("only the room owner can moderate", 3000);
        return;
      }
    }

    // /whisper @name text — visual-only whisper to a room user.
    const wm = trimmed.match(/^\/whisper\s+@?([\w.-]+)\s+(.+)$/i);
    if (wm) {
      const target = users.find(
        (u) => u.username.toLowerCase() === wm[1]!.toLowerCase(),
      );
      if (target) {
        onWhisper?.(target.id, wm[2]!);
        clearInput();
      } else {
        // Keep the message — don't nuke it on a typo.
        showToast(`no user named @${wm[1]}`, 3000);
      }
      return;
    }
    if (/^\/whisper\b/i.test(trimmed)) {
      // "/whisper" with no (or incomplete) text — never send it as chat.
      showToast("whisper someone: /whisper @name message", 3000);
      return;
    }

    if (/^\/lock\b/i.test(trimmed) || /^\/unlock$/i.test(trimmed)) {
      if (!inPrivateRoom) {
        showToast("the lobby can't have a password", 3000);
        clearInput();
        return;
      }
      if (!isOwner) {
        showToast("only the room owner can set a password", 3000);
        clearInput();
        return;
      }
      if (/^\/lock$/i.test(trimmed)) {
        showToast("set a password: /lock your-password", 3000);
        return;
      }
      onSend(trimmed);
      clearInput();
      return;
    }

    exitCommandMode();

    const ok = onSend(trimmed, replyTo?.id);
    if (ok) {
      el.innerHTML = "";
      setIsEmpty(true);
      setStickerOpen(false);
      setReplyTo(null);
      // Sending should pull you back to live chat.
      requestAnimationFrame(scrollToBottom);
    }
  }

  function insertSticker(name: string) {
    if (inputLocked) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();

    if (el.childNodes.length === 1 && el.childNodes[0] instanceof HTMLBRElement) {
      el.innerHTML = "";
    }

    const sel = window.getSelection();
    if (!sel) return;

    if (!sel.rangeCount || !el.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }

    const range = sel.getRangeAt(0);

    if (!el.contains(range.commonAncestorContainer)) {
      range.selectNodeContents(el);
      range.collapse(false);
    }

    let needSpace = false;
    if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset > 0) {
      const prevChar = range.startContainer.textContent?.charAt(range.startOffset - 1) ?? "";
      needSpace = prevChar !== "" && !/\s/.test(prevChar);
    }

    range.deleteContents();

    const img = document.createElement("img");
    img.src = getStickerSrc(name) ?? "";
    img.alt = name;
    img.setAttribute("data-sticker", name);
    img.className = "chat-input-sticker";
    img.draggable = false;
    range.insertNode(img);

    if (needSpace) {
      img.before(document.createTextNode("\u00A0"));
    }
    img.after(document.createTextNode("\u00A0"));

    const after = document.createRange();
    after.setStartAfter(img.nextSibling ?? img);
    after.collapse(true);
    sel.removeAllRanges();
    sel.addRange(after);

    checkEmpty();
  }

  function insertMention(username: string) {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel) return;
    if (!sel.rangeCount || !el.contains(sel.anchorNode)) {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
    document.execCommand("insertText", false, `@${username} `);
    setMentionQuery(null);
    checkEmpty();
  }

  function applyMention(username: string) {
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (!replaceActiveMention(el, username)) {
      // Fallback: append at caret (hover @ button / no active query).
      insertMention(username);
    }
    setMentionQuery(null);
    setMentionIndex(0);
    checkEmpty();
  }

  function syncMentionState() {
    const el = inputRef.current;
    if (!el) {
      setMentionQuery(null);
      return;
    }
    const active = getActiveMention(el);
    if (!active) {
      setMentionQuery(null);
      setMentionIndex(0);
      return;
    }
    setMentionQuery(active.query);
    setMentionIndex(0);
  }

  function handleInput() {
    const el = inputRef.current;
    if (!el) return;

    const content = extractContent(el);
    if (content.length > LIMITS.MAX_MESSAGE_LENGTH) {
      while (extractContent(el).length > LIMITS.MAX_MESSAGE_LENGTH) {
        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) break;
        const range = sel.getRangeAt(0);
        if (range.collapsed) {
          range.setStart(range.startContainer, Math.max(0, range.startOffset - 1));
        }
        range.deleteContents();
      }
    }

    checkEmpty();
    syncMentionState();
    syncCommandState();
  }

  function handleComposerKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    // Slash palette — arrows pick a command, Enter/Tab inserts it.
    if (slashMenu !== null && slashSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSlashIndex((i) => (i + 1) % slashSuggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSlashIndex(
          (i) => (i - 1 + slashSuggestions.length) % slashSuggestions.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const pick = slashSuggestions[slashIndex] ?? slashSuggestions[0];
        if (pick) {
          pickSlashCommand(pick.cmd);
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setSlashMenu(null);
        return;
      }
      return;
    }

    // Command mode — arrows pick a target, Enter picks/runs, Escape cancels.
    if (cmd) {
      if (e.key === "ArrowDown" && cmdSuggestions.length > 0) {
        e.preventDefault();
        setCmdIndex((i) => (i + 1) % cmdSuggestions.length);
        return;
      }
      if (e.key === "ArrowUp" && cmdSuggestions.length > 0) {
        e.preventDefault();
        setCmdIndex(
          (i) => (i - 1 + cmdSuggestions.length) % cmdSuggestions.length,
        );
        return;
      }
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        // Whisper: exact-name fast path ("/whisper alice hello" in one go).
        if (cmd.action === "whisper" && cmd.targets.length === 0) {
          const raw = inputRef.current
            ? extractContent(inputRef.current)
            : "";
          const sp = raw.match(/^\/whisper\s*@?([\w.-]+)\s*([\s\S]*)$/i);
          const name = (sp?.[1] ?? cmdQuery).trim();
          const target = name
            ? users.find(
                (u) => u.username.toLowerCase() === name.toLowerCase(),
              )
            : null;
          if (target) {
            setCmd({ action: "whisper", targets: [target.id] });
            setCmdQuery("");
            setCmdText(sp?.[2]?.trimStart() ?? "");
            clearInput();
            requestAnimationFrame(() => whisperTextRef.current?.focus());
            return;
          }
        }
        const pick = cmdSuggestions[cmdIndex];
        if (pick) {
          addCmdTarget(pick);
        } else if (cmd.targets.length > 0) {
          if (cmd.action === "whisper") {
            whisperTextRef.current?.focus();
          } else {
            onCommand?.(cmd.action, cmd.targets);
            exitCommandMode();
            clearInput();
          }
        }
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        exitCommandMode();
        clearInput();
        return;
      }
      return;
    }
    if (mentionQuery !== null && mentionSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionIndex((i) => (i + 1) % mentionSuggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionIndex(
          (i) => (i - 1 + mentionSuggestions.length) % mentionSuggestions.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        const pick = mentionSuggestions[mentionIndex];
        if (pick) applyMention(pick.username);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionQuery(null);
        return;
      }
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as unknown as FormEvent);
    }
    if (e.key === "Escape" && replyTo) {
      setReplyTo(null);
    }
  }

  useEffect(() => {
    if (!rateLimitUntil || rateLimitUntil <= Date.now()) {
      setToast({ text: "", visible: false });
      return;
    }

    const updateToast = () => {
      const remaining = Math.ceil((rateLimitUntil - Date.now()) / 1000);
      if (remaining <= 0) {
        setToast({ text: "", visible: false });
        return;
      }
      setToast({ text: `Slow down — ${remaining}s`, visible: true });
    };

    updateToast();
    const id = window.setInterval(updateToast, 250);
    return () => window.clearInterval(id);
  }, [rateLimitUntil]);

  function startReply(msg: ChatBroadcastMessage) {
    setReplyTo({
      id: msg.id,
      username: msg.username,
      text: msg.text.slice(0, 120),
    });
    inputRef.current?.focus();
  }

  function renderChatBody(msg: ChatBroadcastMessage | PendingChatMessage) {
    return (
      <>
        {msg.replyTo && (
          <ReplyQuote reply={msg.replyTo} meUsername={me?.username} />
        )}
        <span className="chat-username" style={{ color: msg.color }}>
          <Censored text={msg.username} />
        </span>
        {": "}
        <ChatText
          text={msg.text}
          meUsername={me?.username}
          resolveUser={resolveChatUser}
        />
      </>
    );
  }

  function isAddressedToMe(chat: ChatBroadcastMessage | PendingChatMessage): boolean {
    if (!me) return false;
    const myName = me.username.toLowerCase();
    if (chat.replyTo?.username.toLowerCase() === myName) return true;
    return new RegExp(
      `@${me.username.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`,
      "i",
    ).test(chat.text);
  }

  return (
    <aside className="chat-panel">
      <div
        className="chat-messages"
        ref={listRef}
        role="log"
        aria-live="polite"
        onScroll={handleScroll}
      >
        <div className="chat-messages-inner" ref={contentRef}>
        {messages.map((msg, i) => {
          if (msg.type === "system") {
            if (msg.kind === "poll" || msg.text.startsWith("poll closed:")) {
              return (
                <div
                  key={`sys-${msg.timestamp}-${i}`}
                  className="chat-message system poll-result"
                >
                  <PollClosedCard text={msg.text} />
                </div>
              );
            }
            return (
              <div
                key={`sys-${msg.timestamp}-${i}`}
                className={`chat-message system${msg.kind === "room_password" ? " room-password" : ""}`}
              >
                {msg.kind === "room_password" && (
                  <Warning className="chat-sys-icon" size={22} weight="fill" aria-hidden />
                )}
                <SystemLine text={msg.text} users={users} me={me} />
              </div>
            );
          }

          if (msg.type === "moderation") {
            return (
              <div
                key={`mod-${msg.timestamp}-${i}`}
                className={`chat-message moderation mod-${normalizeModAction(msg.action)}`}
              >
                <span className="chat-mod-mark" aria-hidden="true" />
                <span className="chat-mod-copy">
                <NamedUser name={msg.actorName} users={users} me={me} />
                <span className="chat-mod-verb">
                  {MOD_VERBS[msg.action] ?? msg.action}
                </span>
                {msg.targetNames.map((n, ti) => (
                  <span key={`${n}-${ti}`}>
                    {ti > 0 ? ", " : ""}
                    <NamedUser name={n} users={users} me={me} />
                  </span>
                ))}
                </span>
              </div>
            );
          }

          if (msg.type === "whisper") {
            // Highlight the recipient's own whisper so the one person it's
            // meant for actually notices it (everyone sees it dimmed).
            const toMe =
              me && msg.targetName.toLowerCase() === me.username.toLowerCase();
            const wKey = msg.id;
            if (blocked.has(msg.username.toLowerCase()) && !revealedBlocked.has(wKey)) {
              return (
                <button
                  type="button"
                  key={`whisper-${msg.id}`}
                  className="chat-blocked-chip"
                  onClick={() => revealBlocked(wKey)}
                >
                  <Prohibit size={13} weight="bold" />
                  <span>
                    blocked message from <Censored text={msg.username} /> — click to show
                  </span>
                </button>
              );
            }
            return (
              <div
                key={`whisper-${msg.id}`}
                className={`chat-message whisper${toMe ? " to-me" : ""}`}
              >
                <span className="chat-username" style={{ color: msg.color }}>
                  <Censored text={msg.username} />
                </span>
                <span className="whisper-to">
                  {" "}
                  whispers to{" "}
                  <NamedUser name={msg.targetName} users={users} me={me} />
                  {": "}
                </span>
                <ChatText
                  text={msg.text}
                  meUsername={me?.username}
                  resolveUser={resolveChatUser}
                />
              </div>
            );
          }

          if (msg.type === "pending") {
            const pendingHighlight = isAddressedToMe(msg)
              ? " mentions-me"
              : "";
            if (msg.status === "optimistic") {
              return (
                <div
                  key={msg.clientId}
                  className={`chat-message${pendingHighlight}`}
                >
                  {renderChatBody(msg)}
                </div>
              );
            }

            return (
              <div
                key={msg.clientId}
                className={`chat-message pending status-${msg.status}${pendingHighlight}`}
              >
                {renderChatBody(msg)}
                {msg.status === "sending" ? (
                  <span className="chat-delivery" aria-label="Sending">
                    <span className="chat-mini-spinner" />
                  </span>
                ) : (
                  <span className="chat-delivery failed">
                    <span className="chat-not-delivered">Not delivered</span>
                    {onRetry && (
                      <button
                        type="button"
                        className="chat-retry-btn"
                        onClick={() => onRetry(msg.clientId)}
                      >
                        Retry
                      </button>
                    )}
                  </span>
                )}
              </div>
            );
          }

          const chat = msg as ChatBroadcastMessage;
          const id = chat.id || `legacy-${chat.timestamp}-${i}`;
          const mentionsMe = isAddressedToMe(chat);
          const fromBlocked = blocked.has(chat.username.toLowerCase());
          if (fromBlocked && !revealedBlocked.has(id)) {
            return (
              <button
                type="button"
                key={id}
                className="chat-blocked-chip"
                onClick={() => revealBlocked(id)}
              >
                <Prohibit size={13} weight="bold" />
                <span>
                  blocked message from <Censored text={chat.username} /> — click to show
                </span>
              </button>
            );
          }

          const hasReactions = Object.values(chat.reactions ?? {}).some(
            (ids) => ids.length > 0,
          );
          return (
            <div
              key={id}
              className={`chat-message has-actions${mentionsMe ? " mentions-me" : ""}${hasReactions ? " has-reactions" : ""}`}
            >
              {renderChatBody(chat)}
              <MessageReactions msg={chat} me={me} users={users} onReact={onReact} />
              <div className="chat-msg-actions">
                <button
                  type="button"
                  className="chat-action-btn"
                  title="Reply"
                  onClick={() => startReply(chat)}
                >
                  <ChatTeardropText size={14} weight="bold" />
                </button>
                <button
                  type="button"
                  className="chat-action-btn"
                  title="React"
                  onClick={(e) => {
                    e.stopPropagation();
                    setReactMenuId(reactMenuId === id ? null : id);
                  }}
                >
                  <SmileySticker size={14} weight="fill" />
                </button>
                {me && chat.username !== me.username && (
                  <button
                    type="button"
                    className="chat-action-btn"
                    title={`Mention @${chat.username}`}
                    onClick={() => insertMention(chat.username)}
                  >
                    @
                  </button>
                )}
                {me && chat.username !== me.username && onReport && (
                  <button
                    type="button"
                    className="chat-action-btn"
                    title="Report"
                    aria-label={`Report ${chat.username}`}
                    disabled={reportOutcome?.status === "pending"}
                    onClick={(e) => {
                      e.stopPropagation();
                      setReportingId(reportingId === id ? null : id);
                    }}
                  >
                    <Flag size={14} weight="bold" />
                  </button>
                )}
              </div>
              {reportedId === id && reportOutcome && (
                <div
                  className={`chat-report-status${
                    reportOutcome.status === "failed" ? " failed" : ""
                  }`}
                >
                  {reportOutcome.status === "pending"
                    ? "Sending report…"
                    : reportOutcome.status === "sent"
                      ? "Report sent — thanks"
                      : "Report failed — try again"}
                </div>
              )}
              {reportingId === id && (
                <div
                  className="chat-report-menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  <span className="chat-report-label">
                    Report {chat.username} for
                  </span>
                  {REPORT_REASONS.map((reason) => (
                    <button
                      key={reason}
                      type="button"
                      className="chat-report-option"
                      onClick={() => {
                        const target = users.find(
                          (u) => u.username === chat.username,
                        );
                        if (target) {
                          onReport?.(target.id, reason);
                          setReportingId(null);
                          setReportedId(id);
                        } else {
                          // The author left (or renamed) while the menu was
                          // open — say so instead of silently doing nothing.
                          setReportingId(null);
                          showToast(
                            "That user isn't in the room anymore",
                            3000,
                          );
                        }
                      }}
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              )}
              {reactMenuId === id && (
                <div
                  className="chat-react-menu"
                  onClick={(e) => e.stopPropagation()}
                >
                  {REACTION_EMOJIS.map((emoji) => {
                    const mine =
                      !!me &&
                      (chat.reactions?.[emoji]?.includes(me.id) ?? false);
                    return (
                    <button
                      key={emoji}
                      type="button"
                      className={`chat-react-emoji${mine ? " mine" : ""}`}
                      aria-pressed={mine}
                      onClick={(e) => {
                        e.stopPropagation();
                        onReact?.(id, emoji);
                      }}
                    >
                      {emoji}
                    </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        </div>
      </div>

      <form className={`chat-input-area${inputLocked && chatLocked ? " rate-limited" : ""}`} onSubmit={handleSubmit}>
        {muted && (
          <div className="chat-muted-notice">
            <span className="chat-muted-icon">🔇</span>
            <span>you're muted — the room owner has turned off your mic & chat</span>
          </div>
        )}
        {pollDraft && !inputLocked && (
          <div className="chat-poll-draft">
            <input
              className="chat-poll-title"
              type="text"
              placeholder="poll question"
              maxLength={80}
              value={pollDraft.title}
              onChange={(e) =>
                setPollDraft((d) => (d ? { ...d, title: e.target.value } : d))
              }
            />
            {pollDraft.options.map((opt, i) => (
              <div key={i} className="chat-poll-opt-row">
                <input
                  className="chat-poll-opt"
                  type="text"
                  placeholder={`option ${i + 1}`}
                  maxLength={60}
                  value={opt}
                  onChange={(e) =>
                    setPollDraft((d) =>
                      d
                        ? {
                            ...d,
                            options: d.options.map((o, j) =>
                              j === i ? e.target.value : o,
                            ),
                          }
                        : d,
                    )
                  }
                />
                {pollDraft.options.length > 2 && (
                  <button
                    type="button"
                    className="chat-poll-opt-x"
                    aria-label="Remove option"
                    onClick={() =>
                      setPollDraft((d) =>
                        d
                          ? {
                              ...d,
                              options: d.options.filter((_, j) => j !== i),
                            }
                          : d,
                      )
                    }
                  >
                    <X size={12} weight="bold" />
                  </button>
                )}
              </div>
            ))}
            <div className="chat-poll-actions">
              <button
                type="button"
                className="chat-poll-add"
                disabled={pollDraft.options.length >= 8}
                onClick={() =>
                  setPollDraft((d) =>
                    d ? { ...d, options: [...d.options, ""] } : d,
                  )
                }
              >
                + add option
              </button>
              <button
                type="button"
                className="chat-poll-go"
                disabled={!pollDraft.title.trim() || pollDraft.options.filter((o) => o.trim()).length < 2}
                onClick={() => {
                  onPollCreate?.(
                    pollDraft.title.trim(),
                    pollDraft.options.filter((o) => o.trim()),
                  );
                  setPollDraft(null);
                  clearInput();
                }}
              >
                create poll
              </button>
              <button
                type="button"
                className="chat-poll-cancel"
                onClick={() => {
                  setPollDraft(null);
                  clearInput();
                }}
              >
                cancel
              </button>
            </div>
          </div>
        )}
        {shownPoll && (
          <div className={`chat-poll-panel${pollLeaving ? " leaving" : ""}`}>
            <div className="chat-poll-head">
              <span className="chat-poll-title-text">{shownPoll.title}</span>
              <span className="chat-poll-meta">
                by {shownPoll.createdByName} ·{" "}
                {shownPoll.expiresAt > Date.now()
                  ? `${Math.max(1, Math.ceil((shownPoll.expiresAt - Date.now()) / 60000))}m left`
                  : "closing"}
              </span>
              {!pollLeaving && (isOwner || shownPoll.createdBy === me?.id) && (
                <button
                  type="button"
                  className="chat-poll-close"
                  aria-label="Close poll"
                  onClick={() => onPollClose?.(shownPoll.id)}
                >
                  <X size={14} weight="bold" />
                </button>
              )}
            </div>
            {shownPoll.options.map((label, i) => {
              const votes = shownPoll.votes[i]?.length ?? 0;
              const total = shownPoll.votes.reduce((n, v) => n + v.length, 0);
              const pct = total === 0 ? 0 : Math.round((votes / total) * 100);
              const mine = me ? (shownPoll.votes[i]?.includes(me.id) ?? false) : false;
              return (
                <button
                  key={i}
                  type="button"
                  disabled={pollLeaving}
                  className={`chat-poll-option${mine ? " mine" : ""}`}
                  onClick={() => onPollVote?.(shownPoll.id, i)}
                >
                  <span className="chat-poll-option-label">
                    <Censored text={label} />
                  </span>
                  <span className="chat-poll-option-bar">
                    <span
                      className="chat-poll-option-fill"
                      style={{ width: `${pct}%` }}
                    />
                  </span>
                  <span className="chat-poll-option-pct">
                    {votes} · {pct}%
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {vote && !vote.closed && (
          <div className="chat-vote-panel">
            <div className="chat-vote-head">
              <div className="chat-vote-copy">
                <span className="chat-vote-title-text">
                  {vote.action === "ban" ? "Ban vote" : "Kick vote"}
                </span>
                <span className="chat-vote-sub">
                  {vote.createdByName} wants to {vote.action} {vote.targetName}
                </span>
              </div>
              <span className="chat-poll-meta">
                {Math.max(1, Math.ceil((vote.expiresAt - Date.now()) / 1000))}s left
              </span>
            </div>
            {me && vote.targetUserId === me.id ? (
              <div className="chat-vote-self-note">
                You can't vote on your own removal — the room decides.
              </div>
            ) : (
              <div className="chat-vote-actions">
                <button
                  type="button"
                  className={`chat-vote-btn yes${me && vote.yes.includes(me.id) ? " mine" : ""}`}
                  onClick={() => onVoteCast?.(vote.id, true)}
                >
                  {vote.action === "ban" ? (
                    <Prohibit size={16} weight="bold" />
                  ) : (
                    <Boot size={16} weight="bold" />
                  )}
                  {vote.action === "ban" ? "Ban" : "Kick"}
                </button>
                <button
                  type="button"
                  className={`chat-vote-btn no${me && vote.no.includes(me.id) ? " mine" : ""}`}
                  onClick={() => onVoteCast?.(vote.id, false)}
                >
                  <Check size={16} weight="bold" />
                  Keep
                </button>
              </div>
            )}
            <div className="chat-vote-tally">
              {vote.yes.length} for · {vote.no.length} against
            </div>
          </div>
        )}
        {paused && (
          <div className="chat-paused-wrap">
            <button
              type="button"
              className="chat-paused-pill"
              onClick={scrollToBottom}
            >
              <ArrowDown size={14} weight="bold" aria-hidden="true" />
              <span>
                {unseenCount > 0
                  ? `${unseenCount} new message${unseenCount === 1 ? "" : "s"}`
                  : "Chat paused"}
              </span>
            </button>
          </div>
        )}
        {replyTo && (
          <div className="chat-reply-bar">
            <div className="chat-reply-bar-body">
              <span className="chat-reply-bar-label">
                Replying to <strong>{replyTo.username}</strong>
              </span>
              <span className="chat-reply-bar-text">
                <ChatText
                  text={replyTo.text}
                  meUsername={me?.username}
                  compact
                  resolveUser={resolveChatUser}
                />
              </span>
            </div>
            <button
              type="button"
              className="chat-reply-bar-close"
              aria-label="Cancel reply"
              onClick={() => setReplyTo(null)}
            >
              <X size={14} weight="bold" />
            </button>
          </div>
        )}
        {stickerOpen && (
          <div className="sticker-picker-wrapper">
            <StickerPicker onSticker={(name) => insertSticker(name)} />
          </div>
        )}
        {showSyncBanner && syncLabel && (
          <div className="chat-sync-banner" aria-live="polite">
            <span className="chat-mini-spinner" />
            <span>{syncLabel}</span>
          </div>
        )}
        <div className={`chat-toast${toast.visible ? " visible" : ""}`} aria-live="assertive">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{toast.text}</span>
        </div>
        {cmd && !inputLocked && (
          <div className="chat-cmd-bar">
            <div className="chat-cmd-block">
            <span className="chat-cmd-verb">/{cmd.action}</span>
            {cmd.targets.map((id) => {
              const u =
                users.find((x) => x.id === id) ??
                bannedUsers.find((x) => x.id === id);
              if (!u) return null;
              return (
                <span key={id} className="chat-cmd-chip">
                  {u.avatarSeed ? (
                    <img
                      className="chat-cmd-chip-avatar"
                      src={dylanSrc(u.avatarSeed)}
                      alt=""
                    />
                  ) : (
                    <span className="chat-cmd-chip-avatar banned" aria-hidden="true">
                      🚫
                    </span>
                  )}
                  <Censored text={u.username} />
                  <button
                    type="button"
                    className="chat-cmd-chip-x"
                    aria-label={`Remove ${u.username}`}
                    onClick={() =>
                      setCmd((prev) =>
                        prev
                          ? { ...prev, targets: prev.targets.filter((t) => t !== id) }
                          : prev,
                      )
                    }
                  >
                    <X size={10} weight="bold" />
                  </button>
                </span>
              );
            })}
            </div>
            {cmd.action === "whisper" && cmd.targets.length > 0 && (
              <div className="chat-cmd-whisper">
                <input
                  ref={whisperTextRef}
                  className="chat-cmd-whisper-text"
                  type="text"
                  placeholder={`whisper to ${
                    users.find((u) => u.id === cmd.targets[0])?.username ?? "…"
                  }`}
                  maxLength={200}
                  value={cmdText}
                  onChange={(e) => setCmdText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && cmdText.trim()) {
                      e.preventDefault();
                      sendWhisper();
                    } else if (e.key === "Escape") {
                      e.preventDefault();
                      exitCommandMode();
                      clearInput();
                    }
                  }}
                />
                <button
                  type="button"
                  className="chat-cmd-whisper-send"
                  disabled={!cmdText.trim()}
                  onClick={sendWhisper}
                  aria-label="Send whisper"
                >
                  <PaperPlaneTilt size={16} weight="bold" />
                </button>
              </div>
            )}
            {cmdSuggestions.length > 0 && (
              <div className="chat-cmd-menu" role="listbox">
                {cmdSuggestions.map((u, i) => (
                  <button
                    key={u.id}
                    type="button"
                    role="option"
                    aria-selected={i === cmdIndex}
                    className={`chat-cmd-option${i === cmdIndex ? " active" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      addCmdTarget(u);
                    }}
                  >
                    {u.avatarSeed ? (
                      <img
                        className="chat-cmd-option-avatar"
                        src={dylanSrc(u.avatarSeed)}
                        alt=""
                      />
                    ) : (
                      <span className="chat-cmd-option-avatar banned" aria-hidden="true">
                        🚫
                      </span>
                    )}
                    <Censored text={u.username} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="chat-input-row">
          <button
            type="button"
            className={`sticker-toggle${stickerOpen ? " active" : ""}`}
            onClick={() => setStickerOpen(!stickerOpen)}
            aria-label="Stickers"
            disabled={inputLocked}
          >
            <SmileySticker size={32} weight="fill" />
          </button>
          <div className={`chat-input-editable${isEmpty ? " empty" : ""}${inputLocked ? " locked" : ""}`}>
            {slashMenu !== null && slashSuggestions.length > 0 && (
              <div className="chat-slash-menu" role="listbox" aria-label="Slash commands">
                {slashSuggestions.map((c, i) => (
                  <button
                    key={c.cmd}
                    type="button"
                    role="option"
                    aria-selected={i === slashIndex}
                    className={`chat-slash-option${i === slashIndex ? " active" : ""}`}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pickSlashCommand(c.cmd);
                    }}
                  >
                    <span className="chat-slash-cmd">/{c.cmd}</span>
                    <span className="chat-slash-desc">{c.desc}</span>
                  </button>
                ))}
              </div>
            )}
            {mentionQuery !== null && mentionSuggestions.length > 0 && (
              <div className="chat-mention-menu" role="listbox" aria-label="Mention suggestions">
                {mentionSuggestions.map((u, i) => (
                  <button
                    key={u.id}
                    type="button"
                    role="option"
                    aria-selected={i === mentionIndex}
                    className={`chat-mention-option${i === mentionIndex ? " active" : ""}`}
                    onMouseDown={(e) => {
                      // Prevent contentEditable blur before click applies.
                      e.preventDefault();
                      applyMention(u.username);
                    }}
                  >
                    <span className="chat-mention-option-at">@</span>
                    {u.username}
                  </button>
                ))}
              </div>
            )}
            <div
              ref={inputRef}
              className="chat-input-content"
              contentEditable={!inputLocked && !pollDraft}
              suppressContentEditableWarning
              onInput={handleInput}
              onPaste={(e) => {
                e.preventDefault();
                const text = e.clipboardData.getData("text/plain");
                document.execCommand("insertText", false, text);
                // paste doesn't always fire input in time for mention sync
                requestAnimationFrame(() => {
                  checkEmpty();
                  syncMentionState();
                });
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const text = e.dataTransfer.getData("text/plain");
                if (text) document.execCommand("insertText", false, text);
                requestAnimationFrame(() => {
                  checkEmpty();
                  syncMentionState();
                });
              }}
              onKeyDown={handleComposerKeyDown}
              role="textbox"
              aria-multiline="true"
              aria-label="Chat message"
              aria-autocomplete="list"
            />
          </div>
          <button type="submit" disabled={inputLocked || isEmpty}>
            {hasSending && !isEmpty ? (
              <span className="chat-mini-spinner light" />
            ) : (
              "Send"
            )}
          </button>
        </div>
      </form>
    </aside>
  );
});
